<script setup lang="ts">
import EditableButtonGroup from '@/components/Buttons/EditableButtonGroup.vue'
import { StackLayout } from '@/components/Layouts'
import PermissionWrapper from '@/components/Permission/PermissionWrapper.vue'
import store from '@/store'
import { ECheckpointActions } from '@/store/checkpointStore/CheckpointStoreTypes'
import { EStoreModules } from '@/store/storeType'
import type { CheckpointModel } from '@/types/models/CheckpointModel'
import type { PartnerModel } from '@/types/partners/PartnerModel'
import { ECheckpointsPerm } from '@/types/permissions/CheckpointsPermissionsEnum'
import { isEqual } from 'lodash'
import { computed, inject, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import TypographyWithPrefix from '../../components/Typography/TypographyWithPrefix.vue'
import { EHubContext } from '../../types/context/HubContextEnum'
import { RdrChannelKey } from '../../types/enum/RdrChannelEnum'
import CheckpointActions from './Detail/CheckpointActions.vue'
import CheckpointAssociateDevice from './Detail/CheckpointAssociateDevice.vue'
import CheckpointDetailInformations from './Detail/CheckpointDetailInformations.vue'
import CheckpointPartnerInformations from './Detail/CheckpointPartnerInformations.vue'

type Props = {
    sync?: boolean
    checkpointId: number
    partnerConfig?: PartnerModel
}
const props = defineProps<Props>()
type Emits = {
    (e: 'refresh'): void
    (e: 'refreshAssociate'): void
}
const emit = defineEmits<Emits>()

const route = useRoute()
const eventId = computed(() => parseInt(route.params.event_id.toString()))

const storedCheckpoint = computed(() => store.state.checkpoint)

const data = ref<Partial<CheckpointModel>>({})

const isInEditMode = ref(false)

const getCheckpoint = () =>
    store
        .dispatch(`${EStoreModules.CHECKPOINT}/${ECheckpointActions.FETCH_CHECKPOINT}`, {
            eventId: eventId.value,
            checkpointId: props.checkpointId,
            params: {
                device_configuration: 1,
                device_configuration_filter: [RdrChannelKey]
            }
        })
        .then(res => (data.value = { ...res }))

watch(
    () => props.checkpointId,
    () => getCheckpoint(),
    { immediate: true }
)

const save = () => {
    if (!isEqual(data.value, storedCheckpoint.value.data.checkpoint))
        return store
            .dispatch(`${EStoreModules.CHECKPOINT}/${ECheckpointActions.UPDATE_CHECKPOINT}`, {
                eventId: eventId.value,
                checkpointId: props.checkpointId,
                data: data.value
            })
            .then(() => {
                refreshCheckpoint()
                isInEditMode.value = false
            })
}

const refreshCheckpoint = () => {
    getCheckpoint()
    emit('refresh')
}

const isViewerOnly = inject<boolean>(EHubContext.IS_VIEWER_ONLY, false)
</script>

<template>
    <StackLayout direction="column" :gap="16" isResponsive>
        <PermissionWrapper
            v-if="!sync && !isViewerOnly"
            :permission="ECheckpointsPerm.CHECKPOINTS_EDIT">
            <EditableButtonGroup
                :isInEditMode="isInEditMode"
                @save="save"
                @edit="isInEditMode = $event" />
        </PermissionWrapper>
        <StackLayout direction="row" :gap="8" isResponsive>
            <CheckpointDetailInformations
                :data="data"
                :isInEditMode="isInEditMode"
                @change="data = { ...data, ...$event }">
                <TypographyWithPrefix
                    v-if="partnerConfig"
                    :prefix="$t('event.model.currentPartner')"
                    :label="partnerConfig?.name"
                    boldLabel />
            </CheckpointDetailInformations>
            <StackLayout direction="column" :gap="8" isResponsive class="full-width">
                <CheckpointPartnerInformations
                    v-if="partnerConfig?.config?.checkpoint"
                    :partnerConfig="partnerConfig"
                    :partnerConfigValue="data.partner_config"
                    :isInEditMode="isInEditMode"
                    @change="data = { ...data, ...$event }" />
                <CheckpointActions
                    v-if="storedCheckpoint.data.checkpoint"
                    :data="storedCheckpoint.data.checkpoint" />
            </StackLayout>
        </StackLayout>
        <CheckpointAssociateDevice
            v-if="!isViewerOnly"
            :checkpoint="data"
            @refresh="refreshCheckpoint" />
    </StackLayout>
</template>
